import Link from 'next/link';
import { Dispatch, FC, SetStateAction, type JSX } from 'react';

import { IHeaderDictionary } from '@/dictionaries/components/header/types';
import useWindowSize from '@/hooks/useWindowSize';
import { customLink } from '@/utils';
import { BiBell } from 'react-icons/bi';

import { cn } from '@/lib/utils';
import styles from './styles.module.css';
import useGTMEvents from '@/hooks/useGTMEvents';
import PromotionIcon from '../Icons/Promotion';

type Option = {
  title: string;
  path: string;
  onMouseEnter: () => void;
  onclick?: () => void;
  hasBell: boolean;
  icon?: ({ className }: { className?: string }) => JSX.Element;
  fixUnderline: boolean;
  isDisabled?: boolean;
};

interface MenuOptionsProps {
  dict: IHeaderDictionary;
  lang: string;
  pathname: string;
  setShowVilla: Dispatch<SetStateAction<boolean>>;
  setShowGuide: Dispatch<SetStateAction<boolean>>;
  className?: string;
}

const MenuOptions: FC<MenuOptionsProps> = ({
  dict,
  lang,
  pathname,
  setShowVilla,
  setShowGuide,
  className,
}) => {
  const { width } = useWindowSize();

  const { sendMenuClickEvent } = useGTMEvents();

  const checkVillasHref = () => {
    for (let villa of dict.villaCards) {
      if (pathname.includes(villa.path)) {
        return true;
      }
    }
    return false;
  };

  const checkGuideHref = () => {
    for (let guide of dict.guideCards) {
      if (pathname.includes(guide.path)) {
        return true;
      }
    }
    return false;
  };

  const options: Option[] = [
    {
      title: dict.nav.offers,
      path: dict.nav.offersHref,
      onMouseEnter: () => {
        setShowVilla(false);
        setShowGuide(false);
      },
      hasBell: false,
      icon: PromotionIcon,
      fixUnderline: pathname.includes(dict.nav.offersHref),
    },
    /* {
      title: dict.nav.destination,
      path: '#',
      onMouseEnter: () => {
        setShowVilla(true);
        setShowGuide(false);
      },
      onclick: () => {
        setShowVilla(true);
      },
      hasBell: true,
      fixUnderline: pathname.includes(dict.nav.offersHref)
        ? false
        : checkVillasHref(),
    }, */
    {
      title: dict.nav.villa,
      path: '#',
      onMouseEnter: () => {
        setShowVilla(true);
        setShowGuide(false);
      },
      onclick: () => {
        setShowVilla(true);
      },
      hasBell: false,
      fixUnderline: pathname.includes(dict.nav.offersHref)
        ? false
        : checkVillasHref(),
    },
    {
      title: dict.nav.experience,
      path: dict.nav.experienceHref,
      onMouseEnter: () => {
        setShowVilla(false);
        setShowGuide(false);
      },
      hasBell: false,
      onclick: () =>
        sendMenuClickEvent({
          section: dict.nav.experience,
          sectionItem: 'none',
        }),
      fixUnderline: pathname.includes(dict.nav.experienceHref),
    },
    {
      title: dict.nav.guide,
      path: '#',
      onMouseEnter: () => {
        setShowVilla(false);
        setShowGuide(true);
      },
      hasBell: false,
      fixUnderline: checkGuideHref(),
    },
    {
      title: dict.nav.blog,
      path: dict.nav.blogHref,
      onMouseEnter: () => {
        setShowVilla(false);
        setShowGuide(false);
      },
      onclick: () =>
        sendMenuClickEvent({ section: dict.nav.blog, sectionItem: 'none' }),
      hasBell: false,
      fixUnderline: pathname.includes(dict.nav.blogHref),
    },
    // {
    //   title: dict.nav.about,
    //   path: dict.nav.aboutUsHref,
    //   onMouseEnter: () => {
    //     setShowVilla(false);
    //     setShowGuide(false);
    //   },
    //   hasBell: false,
    //   fixUnderline: pathname.includes(dict.nav.aboutUsHref),
    // },
    {
      title: dict.nav.contact,
      path: dict.nav.contactHref,
      onMouseEnter: () => {
        setShowVilla(false);
        setShowGuide(false);
        sendMenuClickEvent({ section: dict.nav.contact, sectionItem: 'none' });
      },
      onclick: () =>
        sendMenuClickEvent({ section: dict.nav.contact, sectionItem: 'none' }),
      hasBell: false,
      fixUnderline: pathname.includes(dict.nav.contactHref),
    },
  ];

  return (
    <nav
      className={cn(
        'flex flex-col items-start gap-5 text-sm uppercase relative desktop:flex-row desktop:gap-0 transition-colors flex-1 justify-evenly max-w-[800px] px-4',
        className,
      )}>
      {options.map((option: Option) => {
        const OptionIcon = option.icon;
        const fixUnderline = option.fixUnderline;

        if (option.isDisabled) {
          return null;
        }

        const Comp = option.path === '#' ? 'div' : Link;

        return (
          <Comp
            href={customLink(lang, option.path)}
            className="flex relative h-4 z-10"
            onMouseEnter={width > 1200 ? option.onMouseEnter : undefined}
            onClick={option.onclick}
            key={option.title}>
            <div
              className={cn(
                fixUnderline ? styles['underline-fixed'] : styles.underline,
              )}>
              <div>
                <span className="z-10 relative whitespace-nowrap">
                  {option.title}
                  <span className="hidden tablet:inline"> .</span>
                </span>
              </div>
              {option.hasBell && (
                <div className="flex justify-center items-center absolute -top-[20px] desktop:-top-[20px] -right-[16px] desktop:-right-[4px] desktop:left-auto w-[25px] h-[25px] shadow-[0_4px_8px_0_rgba(0,0,0,0.2)] bg-primary rounded-full text-sm text-white z-30">
                  <BiBell />
                </div>
              )}
              {OptionIcon && (
                <div className="flex justify-center items-center absolute -top-[20px] desktop:-top-[20px] -right-[16px] desktop:-right-[4px] desktop:left-auto w-[25px] h-[25px] shadow-[0_4px_8px_0_rgba(0,0,0,0.2)] bg-primary rounded-full text-sm text-white z-30">
                  <OptionIcon className="w-3 h-3" />
                </div>
              )}
            </div>
          </Comp>
        );
      })}
    </nav>
  );
};

export default MenuOptions;
